import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMessageStats } from "../../redux/slices/messageSlice";
import { FiBarChart2 } from "react-icons/fi";

function Dashboard() {
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.auth);
  const { stats, isLoading } = useSelector((state) => state.messages);

  useEffect(() => {
    if (token) dispatch(getMessageStats(token));
  }, [dispatch, token]);

  const channels = [
    {
      label: "SMS",
      value: stats?.sms || 0,
      lastWeek: "0%",
      icon: (
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <rect width="30" height="30" rx="9" fill="#EDE9FE" />
          <path
            d="M8 10h14a1 1 0 011 1v7a1 1 0 01-1 1H10l-3 3V11a1 1 0 011-1z"
            fill="#7C3AED"
          />
        </svg>
      ),
    },
    {
      label: "Email",
      value: stats?.email || 0,
      lastWeek: "0%",
      icon: (
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <rect width="30" height="30" rx="9" fill="#FEF3C7" />
          <rect
            x="7"
            y="10"
            width="16"
            height="11"
            rx="1.5"
            stroke="#D97706"
            strokeWidth="1.5"
          />
          <path
            d="M7 11l8 5 8-5"
            stroke="#D97706"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      label: "Voice",
      value: stats?.voice || 0,
      lastWeek: "0%",
      icon: (
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <rect width="30" height="30" rx="9" fill="#D1FAE5" />
          <path
            d="M12 10c0-.55.45-1 1-1h4c.55 0 1 .45 1 1v5c0 2.76-2.24 5-5 5s-5-2.24-5-5v-2h1v2c0 2.21 1.79 4 4 4s4-1.79 4-4v-5h-4v5H12v-5z"
            fill="#059669"
          />
          <path
            d="M15 20v2M12 22h6"
            stroke="#059669"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      label: "WhatsApp",
      value: stats?.whatsapp || 0,
      lastWeek: "0%",
      icon: (
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <rect width="30" height="30" rx="9" fill="#DCFCE7" />
          <path
            d="M15 7C10.58 7 7 10.58 7 15c0 1.42.37 2.75 1.02 3.91L7 23l4.21-1.1A7.94 7.94 0 0015 23c4.42 0 8-3.58 8-8s-3.58-8-8-8z"
            fill="#16A34A"
          />
          <path
            d="M18.5 16.5c-.3.8-1.4 1.5-2 1.5-1.5 0-4-2.5-4-4 0-.6.7-1.7 1.5-2 .5-.2.8 0 1 .3l.7 1.2c.2.3.1.6-.1.8l-.4.4c.2.5.9 1.2 1.4 1.4l.4-.4c.2-.2.5-.3.8-.1l1.2.7c.3.2.5.5.3 1z"
            fill="#fff"
          />
        </svg>
      ),
    },
    {
      label: "Request / Min.",
      value: 0,
      lastWeek: "0%",
      icon: (
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
          <rect width="30" height="30" rx="9" fill="#FEE2E2" />
          <path
            d="M15 8v3M15 19v3M8 15h3M19 15h3"
            stroke="#DC2626"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <circle cx="15" cy="15" r="3.5" fill="#DC2626" />
        </svg>
      ),
    },
  ];

  const chatStats = [
    { label: "Answered", value: 1, color: "#7C3AED" },
    { label: "Unanswered", value: 0, color: "#E5E7EB" },
    { label: "New", value: 1, color: "#F59E0B" },
    { label: "Repeated", value: 0, color: "#E5E7EB" },
    { label: "Live", value: 0, color: "#E5E7EB" },
  ];

  const liveChats = [
    {
      customer: "Pradeep S.",
      agent: "Unassigned",
      status: "Active",
      message: "Click belo...",
      time: "11:25 AM",
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .db * { box-sizing: border-box; margin: 0; padding: 0; font-family: 'Plus Jakarta Sans', sans-serif; }

        .db {
          background: #F5F6FA;
          min-height: 100vh;
          padding: 28px 30px;
        }

        /* Page title */
        .db-title {
          font-size: 22px;
          font-weight: 800;
          color: #1e1b4b;
          margin-bottom: 4px;
        }

        .db-welcome-bar {
          background: #fff;
          border-radius: 14px;
          padding: 14px 20px;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          border: 1.5px solid #ede9fe;
          margin-bottom: 22px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .db-credit-chip {
          background: linear-gradient(135deg, #7C3AED, #6C63FF);
          color: #fff;
          border-radius: 50px;
          padding: 6px 16px;
          font-size: 13px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        /* Section header */
        .db-section-head {
          font-size: 15px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 14px;
          padding-left: 2px;
          border-left: 3px solid #7C3AED;
          padding-left: 10px;
        }

        /* Channel cards */
        .db-channels {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 14px;
          margin-bottom: 22px;
        }

        @media(max-width:1100px){ .db-channels{ grid-template-columns:repeat(3,1fr); } }

        .db-ch-card {
          background: #fff;
          border-radius: 16px;
          padding: 18px 16px 14px;
          border: 1.5px solid #f0effe;
          box-shadow: 0 2px 8px rgba(124,58,237,0.05);
          transition: all 0.2s ease;
        }

        .db-ch-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(124,58,237,0.1);
          border-color: #c4b5fd;
        }

        .db-ch-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .db-ch-label {
          font-size: 11.5px;
          font-weight: 700;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .db-ch-val {
          font-size: 34px;
          font-weight: 800;
          color: #1e1b4b;
          line-height: 1;
          margin-bottom: 10px;
        }

        .db-ch-footer {
          display: flex;
          align-items: center;
          gap: 5px;
          background: #F5F6FA;
          border-radius: 8px;
          padding: 5px 10px;
          width: fit-content;
        }

        .db-ch-footer span {
          font-size: 11.5px;
          font-weight: 600;
          color: #6b7280;
        }

        /* Bottom grid */
        .db-grid2 {
          display: grid;
          grid-template-columns: 290px 1fr;
          gap: 16px;
          margin-bottom: 18px;
        }

        @media(max-width:900px){ .db-grid2{ grid-template-columns:1fr; } }

        .db-card {
          background: #fff;
          border-radius: 16px;
          padding: 20px;
          border: 1.5px solid #f0effe;
          box-shadow: 0 2px 8px rgba(124,58,237,0.05);
        }

        .db-card-title {
          font-size: 14px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .db-total-line {
          display: flex;
          align-items: baseline;
          gap: 8px;
          margin-bottom: 18px;
        }

        .db-total-num {
          font-size: 44px;
          font-weight: 800;
          color: #7C3AED;
          line-height: 1;
        }

        .db-total-label {
          font-size: 12px;
          color: #9ca3af;
          font-weight: 500;
        }

        .db-bar-row {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }

        .db-bar-lbl {
          font-size: 12.5px;
          color: #6b7280;
          font-weight: 500;
          width: 85px;
          flex-shrink: 0;
        }

        .db-bar-track {
          flex: 1;
          height: 8px;
          background: #f3f4f6;
          border-radius: 99px;
          overflow: hidden;
        }

        .db-bar-fill {
          height: 100%;
          border-radius: 99px;
          transition: width 1s ease;
        }

        .db-bar-val {
          font-size: 12.5px;
          font-weight: 700;
          color: #374151;
          width: 16px;
          text-align: right;
        }

        /* Live chat */
        .db-live-btn {
          background: #7C3AED;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 7px 16px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          font-family: 'Plus Jakarta Sans', sans-serif;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: background 0.2s;
        }

        .db-live-btn:hover { background: #6d28d9; }

        .db-table { width: 100%; border-collapse: collapse; }

        .db-table th {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #9ca3af;
          font-weight: 700;
          padding: 0 12px 12px 0;
          text-align: left;
          border-bottom: 1.5px solid #f3f4f6;
        }

        .db-table td {
          padding: 13px 12px 13px 0;
          font-size: 13px;
          color: #374151;
          border-bottom: 1px solid #f9fafb;
          font-weight: 500;
        }

        .db-table tr:last-child td { border-bottom: none; }

        .db-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 99px;
          font-size: 11px;
          font-weight: 700;
        }

        .db-badge-active { background: #dcfce7; color: #16a34a; }

        .db-cust-link { color: #7C3AED; font-weight: 600; text-decoration: none; }
        .db-cust-link:hover { text-decoration: underline; }

        .db-act-btn {
          background: none;
          border: 1.5px solid #7C3AED;
          color: #7C3AED;
          border-radius: 8px;
          padding: 4px 14px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          font-family: 'Plus Jakarta Sans', sans-serif;
          transition: all 0.2s;
        }

        .db-act-btn:hover { background: #7C3AED; color: #fff; }

        /* Quick links */
        .db-ql-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 18px;
        }

        @media(max-width:800px){ .db-ql-grid{ grid-template-columns:repeat(2,1fr); } }

        .db-ql {
          background: #fff;
          border: 1.5px solid #f0effe;
          border-radius: 14px;
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          text-decoration: none;
          color: #374151;
          font-size: 13px;
          font-weight: 600;
          box-shadow: 0 1px 4px rgba(124,58,237,0.04);
          transition: all 0.2s;
        }

        .db-ql:hover {
          border-color: #7C3AED;
          color: #7C3AED;
          background: #faf5ff;
          transform: translateY(-1px);
        }

        .db-ql-ico {
          width: 34px; height: 34px;
          border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          font-size: 17px;
          flex-shrink: 0;
        }

        /* Latest request empty */
        .db-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 36px 0;
        }

        .db-empty-circle {
          width: 90px; height: 90px;
          border-radius: 50%;
          background: #FEF3C7;
          display: flex; align-items: center; justify-content: center;
          font-size: 38px;
          margin-bottom: 14px;
        }

        .db-empty p {
          font-size: 13.5px;
          font-weight: 600;
          color: #9ca3af;
        }
      `}</style>

      <div className="db">
        {/* Title */}
        <div className="db-title">Dashboard</div>

        {/* Welcome bar */}
        <div className="db-welcome-bar">
          <span>Welcome : {user?.email || "businessonline25@gmail.com"}</span>
          <div className="db-credit-chip">
            💳 INR : {user?.credits || "0"}
          </div>
        </div>

        {/* Today's Report */}
        <div className="db-section-head">Today's Report</div>
        <div className="db-channels">
          {channels.map((ch, i) => (
            <div className="db-ch-card" key={i}>
              <div className="db-ch-top">
                <span className="db-ch-label">{ch.label}</span>
                {ch.icon}
              </div>
              <div className="db-ch-val">{ch.value}</div>
              <div className="db-ch-footer">
                <FiBarChart2 size={12} color="#9ca3af" />
                <span>{ch.lastWeek} last week</span>
              </div>
            </div>
          ))}
        </div>

        {/* Chats + Live Chat */}
        <div className="db-grid2">
          {/* Today's Total Chats */}
          <div className="db-card">
            <div className="db-card-title">
              Today's Total Chats{" "}
              <span style={{ color: "#7C3AED", fontSize: 18 }}>1</span>
            </div>
            <div className="db-total-line">
              <div className="db-total-num">1</div>
              <div className="db-total-label">chat today</div>
            </div>
            {chatStats.map((row) => (
              <div className="db-bar-row" key={row.label}>
                <span className="db-bar-lbl">{row.label}</span>
                <div className="db-bar-track">
                  <div
                    className="db-bar-fill"
                    style={{
                      width: row.value > 0 ? "80%" : "0%",
                      background: row.color,
                    }}
                  />
                </div>
                <span className="db-bar-val">{row.value}</span>
              </div>
            ))}
          </div>

          {/* Live Chat Report */}
          <div className="db-card">
            <div className="db-card-title">
              Live Chat Report
              <button className="db-live-btn">💬 View Live Chat</button>
            </div>
            <table className="db-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Agent</th>
                  <th>Status</th>
                  <th>Message</th>
                  <th>Time</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {liveChats.map((row, i) => (
                  <tr key={i}>
                    <td>
                      <a href="#" className="db-cust-link">
                        {row.customer}
                      </a>
                    </td>
                    <td style={{ color: "#9ca3af" }}>{row.agent}</td>
                    <td>
                      <span className="db-badge db-badge-active">
                        {row.status}
                      </span>
                    </td>
                    <td style={{ color: "#9ca3af" }}>{row.message}</td>
                    <td style={{ color: "#9ca3af" }}>{row.time}</td>
                    <td>
                      <button className="db-act-btn">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Links */}
        <div className="db-section-head">Quick links</div>
        <div className="db-ql-grid">
          {[
            { label: "WhatsApp Widget", emoji: "💬", bg: "#DCFCE7" },
            { label: "RCS Onboarding", emoji: "📣", bg: "#FEF3C7" },
            { label: "API Docs", emoji: "🗂️", bg: "#FEE2E2" },
            { label: "DLT Setup", emoji: "⚙️", bg: "#EDE9FE" },
          ].map((ql) => (
            <a key={ql.label} href="#" className="db-ql">
              <div className="db-ql-ico" style={{ background: ql.bg }}>
                {ql.emoji}
              </div>
              {ql.label}
            </a>
          ))}
        </div>

        {/* Latest Request */}
        <div className="db-section-head">Latest Request</div>
        <div className="db-card">
          <div className="db-empty">
            <div className="db-empty-circle">📋</div>
            <p>No Request Submitted Today</p>
          </div>
        </div>
      </div>
    </>
  );
}

export default Dashboard;
